import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardSection,
  Flex,
  Icon,
  InlineStack,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Text,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import { iconNames } from '@customerio/pluma-icons';
import { objectTypeIcon, type ObjectType } from '@/services/db/object-type/entity';
import { ExportAttributePanel } from './export-attribute-panel';
import * as styles from './objects-export-modal.css';

const OBJECT_REQUIRED = ['object_id'];
const PEOPLE_DEFAULT = ['id', 'email', 'first_name'];
/** Sentinel sent to the backend to mean "all object attributes" for a type. */
const ALL_ATTRIBUTES = ['*'];

/** A section's selection is an explicit attribute list or null for "all". */
type Selection = string[] | null;

/** The entity's icon is a free-form string; narrow to a real icon name (fallback: the generic object icon). */
function objectIconName(type: Pick<ObjectType, 'icon'>): IconName {
  const icon = objectTypeIcon(type);
  return iconNames.find((name) => name === icon) ?? 'object';
}

export type ObjectsExportResult = {
  /** null means "all people attributes". */
  attributes: string[] | null;
  relationshipObjectAttributes: Record<string, string[]> | undefined;
};

export interface ObjectsExportModalData {
  workspaceId: number;
  /** Name-sorted object types the workspace has (each can join the export). */
  objectTypes: readonly ObjectType[];
  /**
   * Initial people selection: an explicit list, or null for "all attributes".
   * Undefined falls back to a sensible default.
   */
  initialPeopleAttributes?: string[] | null;
  onExport: (result: ObjectsExportResult) => void;
}

/**
 * The CSV export configuration modal: one card per source — People always,
 * plus a card for each added object type — each with its own attribute panel.
 * Exporting with object types produces a single combined CSV.
 */
export const ObjectsExportModal = ({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<ObjectsExportModalData>) => {
  const { workspaceId, objectTypes, initialPeopleAttributes, onExport } = data;

  // Distinguish "not provided" (undefined → default) from "all" (null).
  const [peopleAttrs, setPeopleAttrs] = useState<Selection>(
    initialPeopleAttributes === undefined ? [...PEOPLE_DEFAULT] : initialPeopleAttributes,
  );
  const [addedTypeIds, setAddedTypeIds] = useState<readonly string[]>([]);
  const [objectAttrsByType, setObjectAttrsByType] = useState<Record<string, Selection>>({});

  const addedSections = objectTypes
    .filter((type) => addedTypeIds.includes(type.id))
    .map((type) => {
      const raw = objectAttrsByType[type.id];
      const selected: Selection = raw === undefined ? [...OBJECT_REQUIRED] : raw;
      return { type, selected, count: selected === null ? 'All' : String(selected.length) };
    });
  const remainingTypes = objectTypes.filter((type) => !addedTypeIds.includes(type.id));
  const hasObjects = addedTypeIds.length > 0;

  const addType = (type: ObjectType) => {
    setObjectAttrsByType((current) => (type.id in current ? current : { ...current, [type.id]: [...OBJECT_REQUIRED] }));
    setAddedTypeIds((current) => [...current, type.id]);
  };

  const removeType = (typeId: string) => {
    setAddedTypeIds((current) => current.filter((id) => id !== typeId));
    setObjectAttrsByType(({ [typeId]: _removed, ...rest }) => rest);
  };

  // The selection sent to the backend for one object type: null (all) → the
  // "*" sentinel; otherwise the explicit list.
  const relationshipObjectAttributes = (): Record<string, string[]> | undefined => {
    if (addedTypeIds.length === 0) return undefined;
    const result: Record<string, string[]> = {};
    for (const id of addedTypeIds) {
      const selection = id in objectAttrsByType ? objectAttrsByType[id] : [...OBJECT_REQUIRED];
      result[id] = selection === null ? [...ALL_ATTRIBUTES] : (selection ?? [...OBJECT_REQUIRED]);
    }
    return result;
  };

  const handleExport = () => {
    onExport({ attributes: peopleAttrs, relationshipObjectAttributes: relationshipObjectAttributes() });
    onConfirm();
  };

  return (
    <Modal state={modalState} title="Export to CSV" onClose={onClose} className={styles.modal}>
      <ModalBody>
        <Card mb="200">
          <CardSection>
            <InlineStack gap="100" alignBlock="center" mb="150">
              <Icon name="person" color="subtle" size="sm" />
              <Text weight="medium">People attributes</Text>
              <Badge>{peopleAttrs === null ? 'All' : peopleAttrs.length}</Badge>
            </InlineStack>
            <ExportAttributePanel
              workspaceId={workspaceId}
              requiredAttributes={['id']}
              selectedAttributes={peopleAttrs}
              onChange={setPeopleAttrs}
            />
          </CardSection>
        </Card>

        {addedSections.map((section) => (
          <Card key={section.type.id} mb="200">
            <CardSection>
              <Flex alignItems="center" justifyContent="space-between" mb="150">
                <InlineStack gap="100" alignBlock="center">
                  <Icon name={objectIconName(section.type)} color="subtle" size="sm" />
                  <Text weight="medium">{section.type.name}</Text>
                  <Badge>{section.count}</Badge>
                </InlineStack>
                <Button size="sm" variant="secondary" onClick={() => removeType(section.type.id)}>
                  Remove
                </Button>
              </Flex>
              <ExportAttributePanel
                workspaceId={workspaceId}
                objectTypeId={section.type.id}
                objectTypeName={section.type.name}
                requiredAttributes={OBJECT_REQUIRED}
                hiddenAttributes={['cio_object_id']}
                selectedAttributes={section.selected}
                onChange={(attributes) =>
                  setObjectAttrsByType((current) => ({ ...current, [section.type.id]: attributes }))
                }
              />
            </CardSection>
          </Card>
        ))}

        {remainingTypes.length > 0 ? (
          <Flex wrap="wrap" gap="100" alignItems="center">
            <Text color="subtle" mr="100">
              Add related objects:
            </Text>
            {remainingTypes.map((type) => (
              <Button
                key={type.id}
                size="sm"
                variant="secondary"
                icon={objectIconName(type)}
                onClick={() => addType(type)}
              >
                {type.name}
              </Button>
            ))}
          </Flex>
        ) : null}

        {hasObjects ? (
          <Paragraph color="subtle" sizeVariant="xs" mt="100">
            One combined CSV with a row per person–object match: people can appear in multiple rows, and those with no
            match appear once with the object columns left blank.
          </Paragraph>
        ) : null}
      </ModalBody>

      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" ml="100" onClick={handleExport}>
          Export to CSV…
        </Button>
      </ModalFooter>
    </Modal>
  );
};
